import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../../../shared/components/Button.js';
import {
  listMobileScreens,
  saveMobileScreens,
  getMobileTheme,
  saveMobileTheme,
  getMobileExecutionPolicy,
  saveMobileExecutionPolicy,
  getMobileSiteDistance,
  saveMobileSiteDistance,
  listMobileAdminTenants,
  listMobileAdminRoles,
  type AdminScope,
  type MobileAdminRole,
  type MobileScreen,
  type MobileTheme,
  type MobileExecutionPolicy,
  type MobileExecutionPolicyBounds,
  type MobileExecutionPolicyKey,
  type MobileAdminTenant,
  type MobileSiteDistance,
} from '../../api/mobileAdminApi.js';

/**
 * The "Applies to" value meaning "the tenant I am signed in to".
 *
 * The API already understands this without an id: scope 'tenant' with no
 * tenantId is resolved server-side to the caller's own tenant (see scopeOf in
 * mobile-administration.controller.ts). That matters when the tenant list
 * cannot be loaded — without this option the selector offers only "All
 * tenants", so a platform-wide change is the only change that can be made.
 */
const OWN_TENANT = 'tenant';

/**
 * Mobile Administration.
 *
 * Decides what the mobile app shows and how it looks, without rebuilding it.
 * A scope and a role are chosen, screens are ticked, and devices pick the
 * result up on their next sign-in. Settings made for all tenants also reach
 * tenants onboarded later, so a new tenant never starts with an empty menu.
 */
export function MobileAdministrationPage(): ReactNode {
  const { token } = useAuth();

  const [appliesTo, setAppliesTo] = useState<string>('ALL');
  const [tenants, setTenants] = useState<readonly MobileAdminTenant[]>([]);
  // "All tenants" is the platform default; anything else is one tenant. Only a
  // real tenant id travels as tenantId — OWN_TENANT deliberately sends none.
  const scope: AdminScope = appliesTo === 'ALL' ? 'platform' : 'tenant';
  const targetTenantId =
    appliesTo === 'ALL' || appliesTo === OWN_TENANT ? undefined : appliesTo;
  const [roles, setRoles] = useState<readonly MobileAdminRole[]>([]);
  const [roleId, setRoleId] = useState('');
  const [screens, setScreens] = useState<readonly MobileScreen[] | null>(null);
  /** Ticks as edited here, before they are saved. */
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [theme, setTheme] = useState<MobileTheme | null>(null);
  const [themeDraft, setThemeDraft] = useState<Partial<MobileTheme>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');
  const [policy, setPolicy] = useState<MobileExecutionPolicy | null>(null);
  const [policyDraft, setPolicyDraft] = useState<Record<string, string>>({});
  const [siteDistance, setSiteDistance] = useState<MobileSiteDistance | null>(null);
  const [siteDistanceDraft, setSiteDistanceDraft] = useState('');

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const list = await listMobileAdminTenants(token);
        if (alive) setTenants(list);
      } catch {
        /* The tenant list is a convenience, not the contract: without it the
           selector still offers "All tenants" and OWN_TENANT, which is every
           scope the API accepts. */
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const current = await getMobileExecutionPolicy(token);
        if (alive) {
          setPolicy(current);
          setPolicyDraft(policyDraftOf(current));
        }
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  // The roles follow "Applies to": the product roles for all tenants, the
  // chosen tenant's own roles for one tenant. Loading the signed-in admin's
  // own roles once kept every tick on TradeLogic's role ids, where no other
  // tenant's user would ever meet it.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const roleList = await listMobileAdminRoles(
          scope,
          token,
          targetTenantId,
        );
        if (!alive) return;
        setRoles(roleList);
        setRoleId((prev) =>
          roleList.some((r) => r.roleId === prev)
            ? prev
            : (roleList[0]?.roleId ?? ''),
        );
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [scope, targetTenantId, token]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const current = await getMobileTheme(scope, token, targetTenantId);
        if (alive) {
          setTheme(current);
          setThemeDraft({});
        }
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [scope, targetTenantId, token]);

  // The previous-painting distance follows "Applies to", as the theme does.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const current = await getMobileSiteDistance(scope, token, targetTenantId);
        if (alive) {
          setSiteDistance(current);
          setSiteDistanceDraft(String(current.metres));
        }
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [scope, targetTenantId, token]);

  const loadScreens = useCallback(async () => {
    if (!roleId) return;
    setScreens(null);
    setDraft({});
    setError(null);
    try {
      const r = await listMobileScreens(roleId, scope, token, targetTenantId);
      setScreens(r.items);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [roleId, scope, targetTenantId, token]);

  useEffect(() => {
    void loadScreens();
  }, [loadScreens]);

  const isTicked = useCallback(
    (s: MobileScreen) => draft[s.mobileScreenId] ?? s.isVisible,
    [draft],
  );

  const sections = useMemo(() => {
    if (!screens) return null;
    const q = filter.trim().toLowerCase();
    const visible = q
      ? screens.filter(
          (s) =>
            s.label.toLowerCase().includes(q) ||
            s.route.toLowerCase().includes(q),
        )
      : screens;

    const grouped = new Map<string, MobileScreen[]>();
    for (const s of visible) {
      const list = grouped.get(s.sectionCode) ?? [];
      list.push(s);
      grouped.set(s.sectionCode, list);
    }
    return [...grouped.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [screens, filter]);

  const pendingCount = Object.keys(draft).length;
  const tickedCount = screens ? screens.filter(isTicked).length : 0;

  function toggle(screen: MobileScreen, next: boolean): void {
    setDraft((prev) => {
      const copy = { ...prev };
      // Dropping a change that matches what the server already holds keeps the
      // "unsaved" count honest.
      if (next === screen.isVisible) delete copy[screen.mobileScreenId];
      else copy[screen.mobileScreenId] = next;
      return copy;
    });
  }

  function setAllInSection(list: readonly MobileScreen[], next: boolean): void {
    setDraft((prev) => {
      const copy = { ...prev };
      for (const s of list) {
        if (next === s.isVisible) delete copy[s.mobileScreenId];
        else copy[s.mobileScreenId] = next;
      }
      return copy;
    });
  }

  async function save(): Promise<void> {
    if (pendingCount === 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const changes = Object.entries(draft).map(([mobileScreenId, isVisible]) => ({
        mobileScreenId,
        isVisible,
      }));
      const r = await saveMobileScreens(roleId, scope, changes, token, targetTenantId);
      setNotice(
        scope === 'platform'
          ? `Saved ${r.updated} change${r.updated === 1 ? '' : 's'} for every tenant, including ones added later. Devices pick this up on the next sign-in.`
          : `Saved ${r.updated} change${r.updated === 1 ? '' : 's'}. Devices pick this up on the next sign-in.`,
      );
      await loadScreens();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function saveTheme(): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await saveMobileTheme(scope, themeDraft, token, targetTenantId);
      setTheme(saved);
      setThemeDraft({});
      setNotice(
        scope === 'platform'
          ? 'Mobile theme saved for every tenant that has not set its own.'
          : 'Mobile theme saved for this tenant.',
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const themeValue = <K extends keyof MobileTheme>(key: K): string =>
    String(themeDraft[key] ?? theme?.[key] ?? '');

  async function saveSiteDistance(): Promise<void> {
    if (!siteDistance) return;
    const n = Number(siteDistanceDraft);
    const label = 'Previous painting distance';
    if (siteDistanceDraft.trim() === '' || !Number.isInteger(n)) {
      setError(`${label} must be a whole number.`);
      return;
    }
    const b = siteDistance.bounds;
    if (b.min != null && n < b.min) {
      setError(`${label} must be at least ${b.min.toLocaleString()}.`);
      return;
    }
    if (b.max != null && n > b.max) {
      setError(`${label} must be at most ${b.max.toLocaleString()}.`);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await saveMobileSiteDistance(scope, n, token, targetTenantId);
      setSiteDistance(saved);
      setSiteDistanceDraft(String(saved.metres));
      setNotice(
        scope === 'platform'
          ? 'Previous painting distance saved for every tenant that has not set its own.'
          : 'Previous painting distance saved for this tenant.',
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const siteDistanceDirty =
    siteDistance != null && siteDistanceDraft.trim() !== String(siteDistance.metres);

  async function savePolicy(): Promise<void> {
    if (!policy) return;
    const changes: Record<string, number> = {};
    for (const { key, label } of POLICY_FIELDS) {
      const raw = policyDraft[key];
      if (raw === undefined) continue;
      const n = Number(raw);
      if (!Number.isInteger(n)) {
        setError(`${label} must be a whole number.`);
        return;
      }
      const b = policy.bounds[key];
      if (b?.min != null && n < b.min) {
        setError(`${label} must be at least ${b.min.toLocaleString()}.`);
        return;
      }
      if (b?.max != null && n > b.max) {
        setError(`${label} must be at most ${b.max.toLocaleString()}.`);
        return;
      }
      if (n !== (policy[key] as number)) changes[key] = n;
    }
    if (Object.keys(changes).length === 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await saveMobileExecutionPolicy(changes, token);
      setPolicy(saved);
      setPolicyDraft(policyDraftOf(saved));
      setNotice(
        'Field execution policy saved for the whole platform. Field devices apply it within a minute.',
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const policyDirty =
    policy != null &&
    POLICY_FIELDS.some(
      ({ key }) =>
        policyDraft[key] !== undefined &&
        policyDraft[key].trim() !== String(policy[key]),
    );

  return (
    <div data-testid="screen-mobile-administration" style={styles.page}>
      <h1 style={styles.h1}>Mobile Administration</h1>
      <p style={styles.lead}>
        Tick the screens each role should see in the mobile app. The app reads
        this on its next sign-in — no new build is needed. Settings for all
        tenants also apply to tenants added later.
      </p>

      {error ? (
        <div role="alert" data-testid="mobile-admin-error" style={styles.error}>
          {error}
        </div>
      ) : null}
      {notice ? (
        <div role="status" data-testid="mobile-admin-notice" style={styles.notice}>
          {notice}
        </div>
      ) : null}

      <section style={styles.card}>
        <div style={styles.row}>
          <label style={styles.field}>
            <span style={styles.label}>Applies to</span>
            <select
              data-testid="mobile-admin-scope"
              value={appliesTo}
              onChange={(e) => setAppliesTo(e.target.value)}
              style={styles.input}
            >
              <option value="ALL">All tenants (including new ones)</option>
              {tenants.length === 0 ? (
                <option value={OWN_TENANT}>This tenant</option>
              ) : null}
              {tenants.map((tn) => (
                <option key={tn.tenantId} value={tn.tenantId}>
                  {tn.tenantName
                    ? `${tn.tenantName} (${tn.tenantCode})`
                    : tn.tenantCode}
                  {tn.isPlatform ? ' — platform' : ''}
                </option>
              ))}
            </select>
          </label>

          <label style={styles.field}>
            <span style={styles.label}>Role</span>
            <select
              data-testid="mobile-admin-role"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              style={styles.input}
            >
              {roles.map((r) => (
                <option key={r.roleId} value={r.roleId}>
                  {r.roleName}
                </option>
              ))}
            </select>
          </label>

          <label style={styles.field}>
            <span style={styles.label}>Find a screen</span>
            <input
              data-testid="mobile-admin-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="painter, /vendor/photos…"
              style={styles.input}
            />
          </label>

          <div style={styles.actions}>
            <span data-testid="mobile-admin-count" style={styles.count}>
              {tickedCount} of {screens?.length ?? 0} screens on
              {pendingCount > 0 ? ` · ${pendingCount} unsaved` : ''}
            </span>
            <Button
              data-testid="mobile-admin-save"
              onClick={() => void save()}
              disabled={busy || pendingCount === 0}
              style={pendingCount > 0 ? styles.primary : styles.primaryOff}
            >
              Save changes
            </Button>
          </div>
        </div>
      </section>

      {screens === null ? (
        <p data-testid="mobile-admin-loading">Loading…</p>
      ) : sections && sections.length === 0 ? (
        <p data-testid="mobile-admin-empty">No screen matches that search.</p>
      ) : (
        sections?.map(([section, list]) => (
          <section
            key={section}
            data-testid={`mobile-admin-section-${section}`}
            style={styles.card}
          >
            <div style={styles.sectionHead}>
              <h2 style={styles.h2}>{section}</h2>
              <span>
                <Button
                  onClick={() => setAllInSection(list, true)}
                  style={styles.link}
                >
                  All on
                </Button>
                <Button
                  onClick={() => setAllInSection(list, false)}
                  style={styles.link}
                >
                  All off
                </Button>
              </span>
            </div>
            <ul style={styles.list}>
              {list.map((s) => (
                <li key={s.mobileScreenId} style={styles.item}>
                  <label style={styles.checkRow}>
                    <input
                      type="checkbox"
                      data-testid={`mobile-screen-${s.screenCode}`}
                      checked={isTicked(s)}
                      onChange={(e) => toggle(s, e.target.checked)}
                    />
                    <span style={styles.itemLabel}>{s.label}</span>
                    <code style={styles.route}>{s.route}</code>
                    {s.inheritedFromPlatform ? (
                      <span
                        style={styles.inherited}
                        title="Comes from the setting that applies to all tenants"
                      >
                        all tenants
                      </span>
                    ) : !s.isExplicit ? (
                      <span style={styles.muted} title="No decision recorded yet">
                        default
                      </span>
                    ) : null}
                  </label>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      <section style={styles.card} data-testid="mobile-admin-theme">
        <h2 style={styles.h2}>Mobile theme</h2>
        <p style={styles.lead}>
          Applied to every device signed in to this tenant.
        </p>
        <div style={styles.row}>
          <label style={styles.field}>
            <span style={styles.label}>App name</span>
            <input
              data-testid="mobile-theme-app-name"
              value={themeValue('appName')}
              onChange={(e) =>
                setThemeDraft((d) => ({ ...d, appName: e.target.value }))
              }
              style={styles.input}
            />
          </label>
          <label style={styles.field}>
            <span style={styles.label}>Theme colour</span>
            <input
              type="color"
              data-testid="mobile-theme-colour"
              value={themeValue('themeColor') || '#0176D3'}
              onChange={(e) =>
                setThemeDraft((d) => ({ ...d, themeColor: e.target.value }))
              }
              style={styles.colour}
            />
          </label>
          <label style={styles.field}>
            <span style={styles.label}>Splash colour</span>
            <input
              type="color"
              data-testid="mobile-theme-splash"
              value={themeValue('splashColor') || '#FFFFFF'}
              onChange={(e) =>
                setThemeDraft((d) => ({ ...d, splashColor: e.target.value }))
              }
              style={styles.colour}
            />
          </label>
          <label style={styles.field}>
            <span style={styles.label}>Logo URL</span>
            <input
              data-testid="mobile-theme-logo"
              value={themeValue('logoUrl')}
              onChange={(e) =>
                setThemeDraft((d) => ({ ...d, logoUrl: e.target.value }))
              }
              style={styles.input}
            />
          </label>
          <div style={styles.actions}>
            <Button
              data-testid="mobile-theme-save"
              onClick={() => void saveTheme()}
              disabled={busy || Object.keys(themeDraft).length === 0}
              style={
                Object.keys(themeDraft).length > 0
                  ? styles.primary
                  : styles.primaryOff
              }
            >
              Save theme
            </Button>
          </div>
        </div>
      </section>

      <section style={styles.card} data-testid="mobile-admin-site-distance">
        <h2 style={styles.h2}>Previous painting distance</h2>
        <p style={styles.lead}>
          When a painting request is raised, the person raising it and every
          approver are shown the painting already done, or still in progress,
          for the same dealer and within this distance of the site — with how
          long ago it was done, or the approval level it is pending at. The
          approver decides; nothing is refused. Follows &quot;Applies to&quot;
          above.
        </p>
        <div style={styles.row}>
          <label style={styles.field}>
            <span style={styles.label}>Distance (metres)</span>
            <input
              type="number"
              min={siteDistance?.bounds.min ?? undefined}
              max={siteDistance?.bounds.max ?? undefined}
              step={1}
              data-testid="site-distance-metres"
              title={siteDistance?.bounds.parameterCode ?? undefined}
              value={siteDistanceDraft}
              onChange={(e) => setSiteDistanceDraft(e.target.value)}
              style={styles.input}
            />
          </label>
          <div style={styles.actions}>
            <Button
              data-testid="site-distance-save"
              onClick={() => void saveSiteDistance()}
              disabled={busy || !siteDistanceDirty}
              style={siteDistanceDirty ? styles.primary : styles.primaryOff}
            >
              Save distance
            </Button>
          </div>
        </div>
        {siteDistance ? (
          <p style={styles.lead} data-testid="site-distance-limits">
            <code>{siteDistance.bounds.parameterCode}</code>:{' '}
            {describeLimits(siteDistance.bounds)}
          </p>
        ) : null}
      </section>

      <section
        style={styles.card}
        data-testid="mobile-admin-execution-policy"
      >
        <h2 style={styles.h2}>Field execution policy</h2>
        <p style={styles.lead}>
          Platform-wide rules for every tenant, applied by field devices within
          about a minute with no new app build. The geofence radius is how far (in
          metres) a worker may be from the job site and still start work. The
          photo minimums are how many execution photos, per category, a worker
          must upload before a job can be completed. The completion remark
          lengths bound the worker's description of the finished work.
        </p>
        <div style={styles.row}>
          {POLICY_FIELDS.map(({ key, label, unit }) => (
            <label key={key} style={styles.field}>
              <span style={styles.label}>
                {label} ({unit})
              </span>
              <input
                type="number"
                min={policy?.bounds?.[key]?.min ?? undefined}
                max={policy?.bounds?.[key]?.max ?? undefined}
                step={1}
                data-testid={`policy-${key}`}
                title={policy?.bounds?.[key]?.parameterCode ?? undefined}
                value={policyDraft[key] ?? ''}
                onChange={(e) =>
                  setPolicyDraft((d) => ({ ...d, [key]: e.target.value }))
                }
                style={styles.input}
              />
            </label>
          ))}
          <div style={styles.actions}>
            <Button
              data-testid="mobile-policy-save"
              onClick={() => void savePolicy()}
              disabled={busy || !policyDirty}
              style={policyDirty ? styles.primary : styles.primaryOff}
            >
              Save policy
            </Button>
          </div>
        </div>
        {policy ? (
          <div style={styles.lead} data-testid="mobile-policy-hint">
            <p>
              These limits come from the parameter catalogue, so System Config
              enforces exactly the same ones on the same parameters. Field
              workers see these targets on the capture screen as they shoot.
            </p>
            <ul>
              {POLICY_FIELDS.map(({ key, label }) => (
                <li key={key} data-testid={`policy-limits-${key}`}>
                  {label} — <code>{policy.bounds[key].parameterCode}</code>:{' '}
                  {describeLimits(policy.bounds[key])}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    </div>
  );
}

const POLICY_FIELDS: ReadonlyArray<{ key: MobileExecutionPolicyKey; label: string; unit: string }> = [
  { key: 'geofenceRadiusM', label: 'Start-work geofence radius', unit: 'metres' },
  { key: 'photoMinBefore', label: 'Min BEFORE photos', unit: 'photos' },
  { key: 'photoMinDuring', label: 'Min DURING photos', unit: 'photos' },
  { key: 'photoMinAfter', label: 'Min AFTER photos', unit: 'photos' },
  { key: 'photoMinLocation', label: 'Min LOCATION photos', unit: 'photos' },
  { key: 'photoMinDealerCertificate', label: 'Min DEALER CERTIFICATE photos', unit: 'photos' },
  { key: 'completionRemarkMinLength', label: 'Min completion remark length', unit: 'characters' },
  { key: 'completionRemarkMaxLength', label: 'Max completion remark length', unit: 'characters' },
];

function policyDraftOf(p: MobileExecutionPolicy): Record<string, string> {
  return Object.fromEntries(
    POLICY_FIELDS.map(({ key }) => [key, String(p[key])]),
  );
}

/**
 * Says what the catalogue permits for one parameter — and says plainly when it
 * permits anything, rather than showing a limit that is not really in force.
 */
function describeLimits(b: MobileExecutionPolicyBounds): string {
  const d = b.default == null ? 'none' : b.default.toLocaleString();
  if (b.min == null && b.max == null) return `default ${d}, no limit set`;
  if (b.min != null && b.max != null)
    return `default ${d}, allowed ${b.min.toLocaleString()}–${b.max.toLocaleString()}`;
  if (b.min != null) return `default ${d}, minimum ${b.min.toLocaleString()}`;
  return `default ${d}, maximum ${(b.max as number).toLocaleString()}`;
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: 24, maxWidth: 1100 },
  h1: { fontSize: 24, fontWeight: 700, marginBottom: 4 },
  h2: { fontSize: 16, fontWeight: 600 },
  lead: { color: 'var(--color-text-muted)', marginBottom: 16 },
  card: {
    border: '1px solid var(--color-border-default)',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    background: 'var(--color-bg-surface)',
  },
  row: { display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' },
  field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200 },
  label: { fontSize: 13, color: 'var(--color-text-muted)' },
  input: {
    border: '1px solid var(--color-border-default)',
    borderRadius: 6,
    padding: '8px 10px',
    fontSize: 14,
  },
  colour: { width: 64, height: 36, padding: 2, border: '1px solid var(--color-border-default)', borderRadius: 6 },
  actions: { display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' },
  count: { fontSize: 13, color: 'var(--color-text-muted)' },
  primary: {
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 6,
    padding: '9px 16px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  primaryOff: {
    background: 'var(--color-border-default)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 6,
    padding: '9px 16px',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  sectionHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  link: {
    background: 'none',
    border: 'none',
    color: 'var(--color-text-link)',
    cursor: 'pointer',
    fontSize: 13,
    marginLeft: 8,
  },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  item: { borderTop: '1px solid var(--color-border-default)' },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 0',
    cursor: 'pointer',
  },
  itemLabel: { fontSize: 14 },
  route: { fontSize: 12, color: 'var(--color-text-muted)' },
  muted: { fontSize: 11, color: 'var(--color-text-muted)', border: '1px solid var(--color-border-default)', borderRadius: 10, padding: '1px 6px' },
  inherited: {
    fontSize: 11,
    color: 'var(--color-text-link)',
    background: 'var(--color-info-bg)',
    border: '1px solid var(--color-border-default)',
    borderRadius: 10,
    padding: '1px 6px',
  },
  error: {
    background: 'var(--color-danger-bg)',
    border: '1px solid var(--color-danger)',
    color: 'var(--color-danger)',
    borderRadius: 6,
    padding: 12,
    marginBottom: 12,
  },
  notice: {
    background: 'var(--color-success-bg)',
    border: '1px solid var(--color-success)',
    color: 'var(--color-success)',
    borderRadius: 6,
    padding: 12,
    marginBottom: 12,
  },
};
